import { App, Button, Dropdown } from "antd";
import type { TableProps } from "antd";
import {
  useCallback,
  useMemo,
  useState,
  type ComponentType,
  type Dispatch,
  type MouseEvent,
  type SetStateAction,
} from "react";
import { useTranslation } from "react-i18next";
import CrossIcon from "sipli-fleet-core/assets/icons/cross-icon.svg";
import DeleteIcon from "sipli-fleet-core/assets/icons/delete-icon.svg";
import EditIcon from "sipli-fleet-core/assets/icons/edit-icon.svg";
import MenuIcon from "sipli-fleet-core/assets/icons/menu-icon.svg";
import SearchIcon from "sipli-fleet-core/assets/icons/search-icon.svg";
import ActiveIcon from "sipli-fleet-core/assets/icons/active-icon.svg";
import {
  CustomButton,
  CustomTable,
  CustomTooltip,
  CustomTypography,
} from "sipli-fleet-core/components";
import CustomSharedConfirmationModal from "sipli-fleet-core/components/CustomSharedConfirmationModal/CustomSharedConfirmationModal";
import CustomSharedInput from "sipli-fleet-core/components/CustomSharedInput/CustomSharedInput";
import {
  useMutationDeleteTirePreset,
  useMutationUpdateTirePresetStatus,
} from "src/queries/administrator/tirePreset.queries";
import type { TirePresetRecord } from "src/services/administrator/tirePresetService";
import { hasAdministrationPermission } from "src/util/administrationPermission";

interface TirePresetsTableProps {
  data: TirePresetRecord[];
  isLoading?: boolean;
  pagination: {
    page: number;
    size: number;
    total?: number;
  };
  setPagination: Dispatch<SetStateAction<{ page: number; size: number }>>;
  sorting: {
    sortBy: string | undefined;
    sortDir: "ASC" | "DESC" | undefined;
  };
  setSorting: Dispatch<
    SetStateAction<{
      sortBy: string | undefined;
      sortDir: "ASC" | "DESC" | undefined;
    }>
  >;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  onEdit: (record: TirePresetRecord) => void;
}

const CrossIconComponent = CrossIcon as unknown as ComponentType<{
  className?: string;
}>;
const DeleteIconComponent = DeleteIcon as unknown as ComponentType<{
  className?: string;
}>;
const EditIconComponent = EditIcon as unknown as ComponentType<{
  className?: string;
}>;
const MenuIconComponent = MenuIcon as unknown as ComponentType<{
  className?: string;
}>;
const SearchIconComponent = SearchIcon as unknown as ComponentType<{
  className?: string;
}>;
const ActiveIconComponent = ActiveIcon as unknown as ComponentType<{
  className?: string;
}>;

const { CustomText } = CustomTypography;

const dataIndexToColumnId: Record<string, string> = {
  name: "NAME",
  active: "STATUS",
  status: "STATUS",
};

const isActive = (value: unknown) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    return ["active", "true", "1"].includes(value.toLowerCase());
  }
  return false;
};

const StatusBadge = ({ active }: { active?: unknown }) => {
  const { t } = useTranslation();
  const activeStatus = isActive(active);

  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-black">
      {activeStatus ? (
        <ActiveIconComponent className="!cursor-default w-[10px] h-[10px]" />
      ) : (
        <span className="h-[10px] w-[10px] rounded-full bg-gray6" />
      )}
      {activeStatus ? t("active") : t("inactive")}
    </span>
  );
};

const TirePresetsTable = ({
  data,
  isLoading = false,
  pagination,
  setPagination,
  sorting,
  setSorting,
  search,
  setSearch,
  onEdit,
}: TirePresetsTableProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [deleteRecord, setDeleteRecord] = useState<TirePresetRecord | null>(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState<string | number | null>(null);
  const [openActionId, setOpenActionId] = useState<string | number | null>(null);

  const canEdit = hasAdministrationPermission("edit");
  const canDelete = hasAdministrationPermission("delete");
  const updateStatusMutation = useMutationUpdateTirePresetStatus();
  const deleteMutation = useMutationDeleteTirePreset();

  const onTableChange: TableProps<TirePresetRecord>["onChange"] = (
    _pagination,
    _filters,
    sorter,
    extra
  ) => {
    if (extra.action === "filter") return;

    const normalizedSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const order = normalizedSorter?.order;
    const dataIndex = normalizedSorter?.field || normalizedSorter?.columnKey;

    if (!order || !dataIndex) {
      setSorting({ sortBy: "ID", sortDir: "DESC" });
      return;
    }

    const sortBy = dataIndexToColumnId[String(dataIndex)];

    if (!sortBy) {
      setSorting({ sortBy: "ID", sortDir: "DESC" });
      return;
    }

    setSorting({
      sortBy,
      sortDir: order === "ascend" ? "ASC" : "DESC",
    });
    setPagination((previous) => ({ ...previous, page: 0 }));
  };

  const handleStatusChange = useCallback(
    async (record: TirePresetRecord) => {
      setStatusUpdatingId(record.id);

      try {
        await updateStatusMutation.mutateAsync({
          id: record.id,
          active: !isActive(record.active),
        });
        setOpenActionId(null);
        message.success(t("tire_preset_status_updated"));
      } catch {
        message.error(t("setup_status_update_failed"));
      } finally {
        setStatusUpdatingId(null);
      }
    },
    [message, t, updateStatusMutation]
  );

  const handleDeleteConfirm = async () => {
    if (!deleteRecord) return;

    try {
      await deleteMutation.mutateAsync(deleteRecord.id);
      message.success(t("tire_preset_deleted"));
      setDeleteRecord(null);
    } catch {
      message.error(t("cannot_delete_item"));
    }
  };

  const renderActions = useCallback(
    (record: TirePresetRecord) => {
      const editDisabled = !canEdit;
      const deleteDisabled = !canDelete;
      const moreDisabled = !canEdit;
      const disabledActionClass =
        "!cursor-not-allowed !opacity-90 hover:!bg-transparent active:!bg-transparent focus:!bg-transparent !bg-transparent";
      const disabledTitle = t("permission_denied");
      const isCurrentStatusLoading =
        updateStatusMutation.isPending && statusUpdatingId === record.id;
      const nextStatusLabel = isActive(record.active) ? t("inactive") : t("active");

      const handleStatusClick = async (event: MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        await handleStatusChange(record);
      };

      return (
        <div className="flex items-center justify-center gap-2 customer-actions">
          <CustomTooltip title={editDisabled ? disabledTitle : t("edit")}>
            <span className="inline-flex">
              <CustomButton
                type="icon"
                buttonSize="small"
                aria-label={t("edit_tire_preset")}
                disabled={editDisabled}
                className={editDisabled ? disabledActionClass : ""}
                icon={
                  <EditIconComponent
                    className={`w-[16px] h-[16px] ${editDisabled ? "opacity-30 grayscale" : ""}`}
                  />
                }
                onClick={(event) => {
                  event?.stopPropagation?.();
                  onEdit(record);
                }}
              />
            </span>
          </CustomTooltip>

          <CustomTooltip title={deleteDisabled ? disabledTitle : t("delete")}>
            <span className="inline-flex">
              <CustomButton
                type="icon"
                buttonSize="small"
                aria-label={t("delete_tire_preset")}
                disabled={deleteDisabled}
                className={deleteDisabled ? disabledActionClass : ""}
                icon={
                  <DeleteIconComponent
                    className={`w-[15px] h-[16px] ${deleteDisabled ? "opacity-30 grayscale" : ""}`}
                  />
                }
                onClick={(event) => {
                  event?.stopPropagation?.();
                  setDeleteRecord(record);
                }}
              />
            </span>
          </CustomTooltip>

          <CustomTooltip title={moreDisabled ? disabledTitle : t("more_action")}>
            <Dropdown
              disabled={moreDisabled}
              trigger={["click"]}
              open={openActionId === record.id}
              onOpenChange={(open) => setOpenActionId(open ? record.id : null)}
              dropdownRender={() => (
                <div
                  className="min-w-[180px] overflow-hidden rounded-sm bg-white shadow-[0_6px_16px_rgba(0,0,0,0.16)]"
                  onClick={(event) => event.stopPropagation()}
                  onMouseDown={(event) => event.stopPropagation()}
                >
                  <Button
                    type="text"
                    loading={isCurrentStatusLoading}
                    disabled={updateStatusMutation.isPending && !isCurrentStatusLoading}
                    className="!flex !h-[36px] !w-full !cursor-pointer !items-center !justify-start !rounded-none !border-0 !bg-white !px-4 !text-left !text-[14px] !text-black hover:!bg-primary6 focus:!bg-white active:!bg-white focus:!outline-none active:!outline-none"
                    onClick={handleStatusClick}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                  >
                    {nextStatusLabel}
                  </Button>
                </div>
              )}
              placement="bottomRight"
              overlayClassName="customer-action-dropdown"
            >
              <span className="inline-flex">
                <CustomButton
                  type="icon"
                  buttonSize="small"
                  aria-label={t("more_actions")}
                  disabled={moreDisabled}
                  icon={
                    <MenuIconComponent
                      className={`w-[3px] h-[16px] ${moreDisabled ? "opacity-30 grayscale" : ""}`}
                    />
                  }
                  className={`ml-2 !bg-transparent active:!bg-transparent focus:!bg-transparent ${
                    moreDisabled ? disabledActionClass : ""
                  }`}
                />
              </span>
            </Dropdown>
          </CustomTooltip>
        </div>
      );
    },
    [
      canDelete,
      canEdit,
      handleStatusChange,
      onEdit,
      openActionId,
      statusUpdatingId,
      t,
      updateStatusMutation.isPending,
    ]
  );

  const columns: TableProps<TirePresetRecord>["columns"] = useMemo(
    () => [
      {
        title: t("name"),
        dataIndex: "name",
        key: "name",
        sorter: true,
        render: (value: string) => (
          <CustomText className="!text-sm !font-semibold !text-black">{value || "--"}</CustomText>
        ),
      },
      {
        title: t("config"),
        dataIndex: "configuration",
        key: "configuration",
        render: (value: unknown) => {
          const configCount = Array.isArray(value) ? value.length : 0;

          if (configCount > 0) {
            return <span>{t("axis_count", { count: configCount })}</span>;
          }

          return <span>--</span>;
        },
      },
      {
        title: t("status"),
        dataIndex: "active",
        key: "status",
        sorter: true,
        render: (_value, record) => <StatusBadge active={record.active} />,
      },
      {
        title: t("actions"),
        key: "actions",
        width: 120,
        render: (_value, record) => renderActions(record),
      },
    ],
    [renderActions, t]
  );

  return (
    <>
      <div className="flex items-center justify-between gap-3 px-[18px] -mt-2 mb-4">
        <CustomSharedInput
          allowClear={{
            clearIcon: (
              <CrossIconComponent className="mr-0.5 customers-search-clear-icon w-[8.5px] h-[8.5px]" />
            ),
          }}
          prefix={<SearchIconComponent className="customers-search-icon w-[14px] h-[14px]" />}
          placeholder={t("search")}
          value={search}
          className="global-filter-search-class !rounded-[4px]"
          onChange={(event) => {
            setSearch(event.target.value);
            setPagination((previous) => ({ ...previous, page: 0 }));
          }}
        />
      </div>

      <CustomTable<TirePresetRecord>
        customClass="customers-table"
        data={data}
        columns={columns}
        rowKey={(record) => String(record.id)}
        isLoading={isLoading}
        pageSize={pagination.size}
        currentPage={pagination.page}
        totalCount={pagination.total ?? data.length}
        handlePagination={(page) => setPagination((previous) => ({ ...previous, page }))}
        handlePageSizeChange={(size) =>
          setPagination({
            page: 0,
            size: Number(size),
          })
        }
        showSearchInput={false}
        showCustomizeColumns={false}
        showDownload={false}
        sorting={sorting}
        onChange={onTableChange}
        locale={{ emptyText: t("no_data") }}
      />

      <CustomSharedConfirmationModal
        open={Boolean(deleteRecord)}
        title={t("delete_tire_preset")}
        description={t("delete_tire_preset_confirmation")}
        confirmText={t("delete")}
        cancelText={t("cancel")}
        isLoading={deleteMutation.isPending}
        onCancel={() => setDeleteRecord(null)}
        onConfirm={handleDeleteConfirm}
        centered
      />
    </>
  );
};

export default TirePresetsTable;
